import React, { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import { LogOut, Menu } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { LanguageSelector } from "@/components/LanguageSelector"
import { NotificationBell } from "@/components/notifications/NotificationBell"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { getInitials } from "@/lib/utils"
import { HUB_API_BASE, isHubConfigured } from "@/lib/hub-api"
import { useAuthenticatedImageUrl } from "@/hooks/useAuthenticatedImageUrl"
import { simpleGet } from "@/lib/apiUtils"

/**
 * Maps the current pathname to a translation key for the page title.
 */
function getPageTitleKey(pathname: string): string {
	const staticMap: Record<string, string> = {
		"/": "navigation.dashboard",
		"/new": "navigation.newReport",
		"/search": "navigation.searchReports",
		"/templates": "navigation.templates",
		"/users": "navigation.users",
		"/settings": "navigation.settings",
		"/brand": "navigation.brand",
		"/section-library": "navigation.sectionLibrary",
		"/admin": "navigation.admin",
		"/tender-assistant": "navigation.tenderAssistant",
		"/tenders": "navigation.elsMeusPlecs",
		"/public-tenders": "navigation.explorar",
		"/public-tenders/organizations": "navigation.organitzacions",
	}

	if (staticMap[pathname]) return staticMap[pathname]

	if (/^\/public-tenders\/[^/]+$/.test(pathname)) return "navigation.publicTenderDetail"
	if (/^\/templates\/[^/]+\/edit$/.test(pathname)) return "navigation.templates"
	if (/^\/templates\/[^/]+$/.test(pathname)) return "navigation.templates"
	if (/^\/reports\/[^/]+$/.test(pathname)) return "navigation.reportDetail"
	if (/^\/expedients\/[^/]+$/.test(pathname)) return "navigation.expedientDetail"
	return "navigation.dashboard"
}

export function NavHeader() {
	const t = useTranslations()
	const { pathname } = useLocation()
	const { reportsUser, logout, organization } = useAuth()

	const userInitials = getInitials(reportsUser?.name || "User")
	const pageTitle = t("app.title")

	// Fetch the tenant logo. With a Hub configured, use its public branding
	// endpoint (legacy platform deployments). Standalone (no Hub URL) and
	// LOCAL_MODE use the module's own brand endpoint — no Hub network call,
	// no console errors. Falls back silently to /OptimTechLogo.png (rendered
	// via the `||` fallback below) on 404 / network error / pre-auth.
	const [hubLogoUrl, setHubLogoUrl] = useState<string | null>(null)
	const displayHubLogoUrl = useAuthenticatedImageUrl(hubLogoUrl)

	useEffect(() => {
		const tenantId = reportsUser?.organizationId
		if (!tenantId) return
		const controller = new AbortController()

		if (import.meta.env.VITE_LOCAL_MODE === "true" || !isHubConfigured) {
			simpleGet<{ data?: { identity?: { logoUrl?: string | null } } }>(`/api/organizations/${tenantId}/brand`)
				.then((data) => {
					const logoUrl = data?.data?.identity?.logoUrl
					if (logoUrl) setHubLogoUrl(logoUrl)
				})
				.catch(() => {
					// Silent — fall back to OptimTechLogo.png
				})
			return () => controller.abort()
		}

		fetch(`${HUB_API_BASE}/api/branding/${tenantId}`, {
			cache: "no-cache",
			signal: controller.signal,
		})
			.then((r) => (r.ok ? r.json() : null))
			.then((data) => {
				if (data?.logoUrl) setHubLogoUrl(data.logoUrl)
			})
			.catch(() => {
				// Silent — fall back to OptimTechLogo.png
			})
		return () => controller.abort()
	}, [reportsUser?.organizationId])

	const handleLogout = async () => {
		try {
			await logout()
		} catch (error) {
			console.error("Error during logout:", error)
		}
	}

	return (
		<header className='sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b border-border px-4 bg-white backdrop-blur-md'>
			{/* Mobile sidebar trigger */}
			<SidebarTrigger className='md:hidden mr-2'>
				<Menu className='h-5 w-5' />
			</SidebarTrigger>

			{/* Left Section: Org Logo + Page Title */}
			<div className='flex flex-1 items-center gap-4'>
				{/* eslint-disable-next-line @next/next/no-img-element */}
				<img
					src={displayHubLogoUrl || "/OptimTechLogo.png"}
					alt={hubLogoUrl ? (organization?.name ?? "Organization") : "OptimTech"}
					className='h-8 w-auto max-w-35 object-contain'
				/>
				<div>
					<h1 className='text-xl font-bold bg-gradient-to-r from-brand-forest-start via-brand-forest-mid to-brand-forest-end bg-clip-text text-transparent'>{pageTitle}</h1>
					<p className='text-sm text-muted-foreground'>{t("reports.subtitle")}</p>
				</div>
			</div>

			{/* Right Section */}
			<div className='flex items-center gap-4'>
				{/* Language Selector */}
				<div className='hidden md:flex'>
					<LanguageSelector compact={true} />
				</div>

				{/* Notifications */}
				<NotificationBell />

				{/* Divider */}
				<div className='hidden md:block h-8 w-px bg-institutional/10 mx-1' />

				{/* User Avatar — always render shell so it doesn't pop in after auth */}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' className='relative h-10 w-10 rounded-full hover:bg-institutional/5 transition-colors'>
							<Avatar className='h-10 w-10 border-2 border-institutional/20 hover:border-institutional/40 transition-colors shadow-sm'>
								<AvatarFallback className='bg-gradient-to-br from-institutional/10 to-brand-forest-start/10 text-institutional font-semibold text-sm'>{userInitials}</AvatarFallback>
							</Avatar>
						</Button>
					</DropdownMenuTrigger>
					{reportsUser && (
						<DropdownMenuContent align='end' className='w-56 shadow-lg border-institutional/10'>
							<DropdownMenuLabel className='pb-2'>
								<div className='flex flex-col space-y-1'>
									<p className='text-sm font-semibold leading-none text-institutional'>{reportsUser.name}</p>
									<p className='text-xs leading-none text-muted-foreground'>{reportsUser.email}</p>
									<Badge variant='info' className='mt-2 w-fit text-[10px] uppercase tracking-wider'>
										{reportsUser.role.toUpperCase()}
									</Badge>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator className='bg-institutional/10' />
							<DropdownMenuItem onClick={handleLogout} className='text-destructive focus:text-destructive cursor-pointer'>
								<LogOut className='mr-2 h-4 w-4' />
								{t("auth.logout")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					)}
				</DropdownMenu>
			</div>
		</header>
	)
}
